import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import './PaymentSystem.css';

/**
 * PaymentSuccess - 2025 Celebratory Design
 *
 * Users just paid money - make them feel good about it!
 * Includes confetti, clear confirmation, and next steps
 */

// Confetti component
const Confetti = ({ count = 50 }) => {
  const confetti = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 2,
      color: ['#297A92', '#7c3aed', '#10b981', '#f59e0b', '#ef4444'][Math.floor(Math.random() * 5)],
      size: 8 + Math.random() * 8
    }));
  }, [count]);

  return (
    <div className="confetti-container">
      {confetti.map((piece) => (
        <motion.div
          key={piece.id}
          className="confetti"
          initial={{ y: -20, opacity: 1, rotate: 0 }}
          animate={{ y: '100vh', opacity: 0, rotate: 720 }}
          transition={{
            duration: piece.duration,
            delay: piece.delay,
            ease: 'easeOut'
          }}
          style={{
            left: piece.left,
            width: piece.size,
            height: piece.size,
            background: piece.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px'
          }}
        />
      ))}
    </div>
  );
};

const PaymentSuccess = ({ onRefreshUser }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (sessionId) {
      verifyCheckoutSession(sessionId);
    } else {
      setLoading(false);
      setError('Session ID not found. Payment verification failed.');
    }
  }, [location]);

  const verifyCheckoutSession = async (sessionId) => {
    try {
      const response = await axios.get(`/api/payment/verify-session?session_id=${sessionId}`);
      setSessionData(response.data);

      // Refresh user auth status
      try {
        const authResponse = await axios.get('/api/auth/verify');
        if (authResponse.data.valid && authResponse.data.user && onRefreshUser) {
          onRefreshUser(authResponse.data.user);
        }
      } catch (authError) {
        console.warn('Payment succeeded but error refreshing auth:', authError);
      }

      setLoading(false);
      // Trigger confetti after a short delay
      setTimeout(() => setShowConfetti(true), 300);
    } catch (error) {
      console.error('Error verifying checkout session:', error);
      setError(error.response?.data?.error || 'An error occurred while verifying your payment.');
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (sessionData?.tier) {
      localStorage.setItem('latest_subscription_tier', sessionData.tier);
      localStorage.setItem('latest_subscription_data', JSON.stringify({
        tier: sessionData.tier,
        status: 'active',
        subscription_id: sessionData.subscription_id,
        customer: sessionData.customer
      }));
    }
    window.location.href = '/tutor';
  };

  const getTierName = (tier) => {
    const names = {
      unlimited: 'Unlimited',
      home: 'Home',
      premium: 'Premium',
      student: 'Student'
    };
    return names[tier] || tier;
  };

  return (
    <div className="payment-page">
      {showConfetti && <Confetti count={60} />}

      {loading ? (
        <div className="payment-loading">
          <div className="payment-spinner" />
          <p className="payment-loading-text">Verifying your payment...</p>
        </div>
      ) : error ? (
        <motion.div
          className="cancel-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="cancel-icon">❌</div>
          <h1 className="cancel-title">Verification Failed</h1>
          <p className="cancel-subtitle">{error}</p>
          <button className="cancel-cta" onClick={() => navigate('/pricing')}>
            Try Again
          </button>
        </motion.div>
      ) : (
        <motion.div
          className="success-card"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <motion.div
            className="success-icon"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2, stiffness: 200 }}
          >
            ✓
          </motion.div>

          <motion.h1
            className="success-title"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            You're all set!
          </motion.h1>

          <motion.p
            className="success-subtitle"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            Your {getTierName(sessionData?.tier)} plan is now active
          </motion.p>

          {sessionData && (
            <motion.div
              className="success-details"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="success-detail-row">
                <span className="success-detail-label">Plan</span>
                <span className="success-detail-value">{getTierName(sessionData.tier)}</span>
              </div>
              {sessionData.amount && (
                <div className="success-detail-row">
                  <span className="success-detail-label">Amount</span>
                  <span className="success-detail-value">
                    ${(sessionData.amount / 100).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="success-detail-row">
                <span className="success-detail-label">Status</span>
                <span className="success-detail-value" style={{ color: '#10b981' }}>
                  Active
                </span>
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            style={{ width: '100%' }}
          >
            <p style={{
              fontSize: '15px',
              color: 'var(--color-text-secondary, #71717a)',
              marginBottom: '24px',
              lineHeight: 1.6
            }}>
              You now have unlimited access to voice tutoring, screen sharing, and study sessions with friends.
            </p>

            <motion.button
              className="success-cta"
              onClick={handleContinue}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Start learning
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default PaymentSuccess;
